import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getUserFromLocalStorage } from '../utils/auth';
import { branchAPI } from '../services';
import { currentParams, getParam, toSearchString } from '../utils/urlParams';
import { replaceSearch } from '../utils/routerBridge';

const BranchContext = createContext();

// Export BranchContext để sử dụng trong useBranch hook
export { BranchContext };

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

// #120936 — `branch_id` là đòn bẩy lớn nhất của việc deep-link: nó KHÔNG phải state của từng
// trang mà nằm ở context toàn cục này, nên dạy được chỗ này hiểu `?branch_id=` là 37/69 trang
// admin deep-link được theo chi nhánh mà không phải mở trang nào ra sửa.
// Chỉ nhận alias camelCase `branchId` (tên cũ lệch chuẩn, chỉ ĐỌC). CỐ Ý không nhận `branch`:
// `SupplierOrderNeedsPage.js:43` và `ShopeeShoppingListPage.js:35` đã dùng `?branch=` làm bộ lọc
// RIÊNG của trang — nuốt tên đó ở đây sẽ đổi chi nhánh toàn cục ngoài ý muốn.
const BRANCH_PARAM = 'branch_id';
const BRANCH_PARAM_ALIASES = ['branchId'];

const readBranchIdFromUrl = () => getParam(currentParams(), BRANCH_PARAM, BRANCH_PARAM_ALIASES);

const readBranchFromLocalStorage = () => {
  const saved = localStorage.getItem('selectedBranch');
  if (!saved) return null;
  try {
    return JSON.parse(saved);
  } catch (error) {
    console.error('Error parsing saved branch:', error);
    localStorage.removeItem('selectedBranch');
    return null;
  }
};

export const BranchProvider = ({ children }) => {
  // URL thắng localStorage, và phải là LAZY INIT.
  // Trước #120936 chỗ này là `useState(null)` + một `useEffect` khôi phục từ localStorage chạy
  // SAU mount — effect đó sẽ ĐÈ lên chi nhánh vừa lấy từ URL. Đã bỏ hẳn effect, dồn hết vào đây
  // để thứ tự khởi tạo là tất định (mục 4a của KHUON-CHUAN-DEEPLINK.md).
  const [selectedBranch, setSelectedBranch] = useState(() => {
    const idFromUrl = readBranchIdFromUrl();
    if (idFromUrl) {
      // `branches` tải bất đồng bộ nên lúc này chưa có object đầy đủ. Nhận id trước để các trang
      // gọi API được ngay, đánh dấu `pending` và validate lại khi danh sách về (bên dưới).
      return { id: idFromUrl, pending: true };
    }
    return readBranchFromLocalStorage();
  });

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Thông báo khi id trên URL không thuộc quyền người dùng (luật 3c của khuôn).
  const [branchNotice, setBranchNotice] = useState(null);

  // Chi nhánh lấy từ URL thì KHÔNG được để effect tự-chọn ghi đè.
  const cameFromUrlRef = useRef(!!readBranchIdFromUrl());

  const [branchesLoaded, setBranchesLoaded] = useState(false);

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await branchAPI.getBranches({
        is_active: true,
        page: 1,
        limit: 100,
      });

      if (response.data) {
        setBranches(response.data);
      } else {
        setBranches(response);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
      setError(err);
    } finally {
      setLoading(false);
      setBranchesLoaded(true);
    }
  }, []);

  // Lấy danh sách branches khi component mount
  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Memoize accessible branches để tránh re-compute mỗi lần render
  const accessibleBranches = useMemo(() => {
    const user = getUserFromLocalStorage();

    if (!user) {
      return branches;
    }

    // Nếu user là ADMIN, có thể truy cập tất cả branches
    if (user.role === 'ADMIN') {
      return branches;
    }

    // Nếu user có branch_id cụ thể, chỉ trả về branch đó
    if (user.branch_id) {
      return branches.filter((branch) => branch.id === user.branch_id);
    }

    return branches;
  }, [branches]);

  /**
   * Ghi ngược lên URL. Chọn "-- Tất cả chi nhánh --" (null) thì XOÁ HẲN param, không ghi
   * `branch_id=` rỗng. Luôn `replace`, không `push` (luật 1c-2 của khuôn).
   */
  const syncUrl = useCallback((branchId) => {
    const params = currentParams();
    BRANCH_PARAM_ALIASES.forEach((alias) => params.delete(alias));
    if (branchId) {
      params.set(BRANCH_PARAM, branchId);
    } else {
      params.delete(BRANCH_PARAM);
    }
    replaceSearch(toSearchString(params));
  }, []);

  // Validate lại id lấy từ URL KHI danh sách chi nhánh đã về.
  // Luật 3c: id không thuộc `accessibleBranches` thì KHÔNG áp, giữ chi nhánh cũ + báo rõ.
  // Lưu ý: đây KHÔNG phải cặp effect hai chiều — chỉ chạy đúng một lần, lúc danh sách về.
  useEffect(() => {
    if (!branchesLoaded || !selectedBranch?.pending) return;

    const match = accessibleBranches.find((b) => b.id === selectedBranch.id);
    if (match) {
      setSelectedBranch(match);
      setBranchNotice(null);
      return;
    }

    const existsButForbidden = branches.some((b) => b.id === selectedBranch.id);
    setBranchNotice(
      existsButForbidden
        ? 'Bạn không có quyền xem chi nhánh này. Đang giữ chi nhánh hiện tại.'
        : 'Không tìm thấy chi nhánh trong đường dẫn. Đang giữ chi nhánh hiện tại.',
    );

    // Quay về chi nhánh đã lưu trước đó, và dọn param sai khỏi URL.
    const fallback = readBranchFromLocalStorage();
    const allowedFallback =
      fallback && accessibleBranches.some((b) => b.id === fallback.id) ? fallback : null;
    setSelectedBranch(allowedFallback);
    cameFromUrlRef.current = false;
    syncUrl(allowedFallback?.id || null);
  }, [branchesLoaded, selectedBranch, accessibleBranches, branches, syncUrl]);

  // Tự động chọn branch đầu tiên nếu user chỉ có quyền truy cập 1 branch.
  // #120936 — phải NHƯỜNG URL: chỉ chạy khi không có `branch_id` trên URL và không còn id nào
  // đang chờ validate, nếu không sẽ đè lên chi nhánh người dùng vừa dán vào link.
  useEffect(() => {
    if (cameFromUrlRef.current || selectedBranch?.pending) return;
    if (accessibleBranches.length === 1 && !selectedBranch) {
      setSelectedBranch(accessibleBranches[0]);
    }
  }, [accessibleBranches, selectedBranch]);

  /** Người dùng chọn chi nhánh (thao tác thật) → đổi state, lưu localStorage VÀ ghi ngược URL. */
  const selectBranch = useCallback(
    (branch) => {
      cameFromUrlRef.current = false;
      setBranchNotice(null);
      setSelectedBranch(branch);

      if (branch) {
        localStorage.setItem('selectedBranch', JSON.stringify(branch));
      } else {
        localStorage.removeItem('selectedBranch');
      }

      syncUrl(branch?.id || null);
    },
    [syncUrl],
  );

  const value = {
    selectedBranch,
    branches: accessibleBranches,
    allBranches: branches,
    loading,
    error,
    selectBranch,
    refreshBranches: fetchBranches,
    // #120936 — cảnh báo deep-link, hiển thị ở BranchSelector (có mặt trên mọi trang).
    branchNotice,
    dismissBranchNotice: () => setBranchNotice(null),
    // Utility functions
    getSelectedBranchId: () => selectedBranch?.id,
    getSelectedBranchName: () => selectedBranch?.name,
    isBranchSelected: () => !!selectedBranch,
  };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};
